import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Session, Therapist } from '../../../core/types';
import { ROUTES } from '../../../core/data/routes';
import { useAppointmentsStore } from '../store/appointmentsStore';
import {
  AppBar,
  LoadingWidget,
  Dialog,
  FadeInAnimation,
  StatusChip,
} from '../../../core/components/ui';
import {
  formatDate,
  formatTime,
  isWithinWindow,
} from '../../../core/utils/formatters';
import toast from 'react-hot-toast';

const UpcomingDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const session = (location.state as any)?.session as
    | Session
    | undefined;

  const store = useAppointmentsStore();

  const [therapist, setTherapist] =
    useState<Therapist | null>(null);

  const [loading, setLoading] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [ratingOpen, setRatingOpen] = useState(false);

  const [cancelReason, setCancelReason] = useState('');
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');

  useEffect(() => {
    if (!session) {
      navigate(-1);
      return;
    }

    if (session.therapistId) {
      store
        .fetchTherapistInfo(session.therapistId)
        .then(setTherapist);
    }
  }, []);

  if (!session) return null;

  const canJoin = session?.sessionFrom
    ? isWithinWindow(session.sessionFrom)
    : false;

  const isPending =
    session?.status === 'PENDING' ||
    session?.status === 'CONFIRMED';

  const isCompleted =
    session?.status === 'COMPLETED';

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      toast.error('Please provide a reason.');
      return;
    }

    setLoading(true);

    await store.cancelSession(
      session.id!,
      cancelReason
    );

    toast.success('Session cancelled.');

    setCancelOpen(false);
    navigate(-1);

    setLoading(false);
  };

  const handleRate = async () => {
    if (rating === 0) {
      toast.error('Please select a rating.');
      return;
    }

    setLoading(true);

    await store.rateTherapist({
      sessionId: session.id,
      therapistId: session.therapistId,
      rating,
      review,
    });

    toast.success(
      'Thank you for your feedback!'
    );

    setRatingOpen(false);
    setLoading(false);
  };

  return (
    <div
      className="page"
      style={{
        minHeight: '100vh',
        background: 'var(--color-background)',
      }}
    >
      {loading && <LoadingWidget />}

      <AppBar
        title="Session Details"
        onBack={() => navigate(-1)}
      />

      <div
        style={{
          padding: '20px',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: '0 auto',
            display: 'grid',
            gap: 20,
            gridTemplateColumns:
              'repeat(auto-fit, minmax(320px, 1fr))',
            alignItems: 'start',
          }}
        >
          {/* LEFT COLUMN */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
            }}
          >
            {/* Therapist Card */}
            <FadeInAnimation delay={0}>
              <div
                className="card"
                style={{
                  padding: 24,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 18,
                    flexWrap: 'wrap',
                  }}
                >
                  {/* <div
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: '50%',
                      background:
                        'var(--color-primary-container)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: 22,
                      color:
                        'var(--color-on-primary-container)',
                      flexShrink: 0,
                    }}
                  >
                    {(session.therapistName || '?')
                      .split(' ')
                      .map((n) => n[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase()}
                  </div> */}
<div
  style={{
    width: 72,
    height: 72,
    borderRadius: '50%',
    background: 'var(--color-primary-container)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  }}
>
  {therapist?.url ? (
    <img
      src={therapist.url}
      alt={session.therapistName || 'Therapist'}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
      }}
      onError={(e) => {
        e.currentTarget.style.display = 'none';
      }}
    />
  ) : (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--color-on-primary-container)"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 21a8 8 0 0 0-16 0" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )}
</div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 22,
                        fontWeight: 700,
                        marginBottom: 6,
                        color:
                          'var(--color-on-surface)',
                      }}
                    >
                      {session.therapistName ||
                        'Therapist'}
                    </div>

                    {therapist?.organization && (
                      <div
                        style={{
                          fontSize: 14,
                          color:
                            'var(--color-on-surface-variant)',
                          marginBottom: 10,
                        }}
                      >
                        {therapist.organization}
                      </div>
                    )}

                    <StatusChip
                      status={
                        session.status || 'PENDING'
                      }
                    />
                  </div>
                </div>
              </div>
            </FadeInAnimation>

            {/* Schedule */}
            <FadeInAnimation delay={0.1}>
              <div
                className="card"
                style={{
                  padding: 24,
                }}
              >
                <SectionTitle title="Schedule" />

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16,
                  }}
                >
                  <InfoRow
                    icon={<CalIcon />}
                    label="Date"
                    value={
                      session.sessionFrom
                        ? formatDate(
                            session.sessionFrom
                          )
                        : '—'
                    }
                  />

                  <Divider />

                  <InfoRow
                    icon={<TimeIcon />}
                    label="Time"
                    value={
                      session.sessionFrom
                        ? `${formatTime(
                            session.sessionFrom
                          )} ${
                            session.sessionTo
                              ? `- ${formatTime(
                                  session.sessionTo
                                )}`
                              : ''
                          }`
                        : '—'
                    }
                  />
                </div>
              </div>
            </FadeInAnimation>

            {/* Reason */}
            {session.sessionReason && (
              <FadeInAnimation delay={0.2}>
                <div
                  className="card"
                  style={{
                    padding: 24,
                  }}
                >
                  <SectionTitle title="Reason" />

                  <p
                    style={{
                      fontSize: 15,
                      lineHeight: 1.8,
                      color:
                        'var(--color-on-surface)',
                    }}
                  >
                    {session.sessionReason}
                  </p>
                </div>
              </FadeInAnimation>
            )}

            {/* Report */}
            {session.sessionReport && (
              <FadeInAnimation delay={0.25}>
                <div
                  className="card"
                  style={{
                    padding: 24,
                  }}
                >
                  <SectionTitle title="Session Report" />

                  <p
                    style={{
                      fontSize: 15,
                      lineHeight: 1.8,
                      color:
                        'var(--color-on-surface)',
                    }}
                  >
                    {session.sessionReport}
                  </p>
                </div>
              </FadeInAnimation>
            )}
          </div>

          {/* RIGHT COLUMN */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
              position: 'sticky',
              top: 90,
              alignSelf: 'start',
            }}
          >
            <FadeInAnimation delay={0.3}>
              <div
                className="card"
                style={{
                  padding: 24,
                }}
              >
                <SectionTitle title="Actions" />

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                  }}
                >
                  {isPending &&
                    canJoin &&
                    session.meetingLink && (
                      <button
                        className="btn-gradient"
                        style={{
                          width: '100%',
                          height: 52,
                        }}
                        onClick={() =>
                          navigate(
                            ROUTES.CONFERENCE,
                            {
                              state: {
                                session,
                                therapist,
                              },
                            }
                          )
                        }
                      >
                        Join Session
                      </button>
                    )}

                  {isCompleted && (
                    <button
                      className="btn-primary"
                      style={{
                        width: '100%',
                        height: 52,
                      }}
                      onClick={() =>
                        setRatingOpen(true)
                      }
                    >
                      Rate Session
                    </button>
                  )}

                  {isPending && (
                    <button
                      onClick={() =>
                        setCancelOpen(true)
                      }
                      style={{
                        width: '100%',
                        height: 52,
                        borderRadius: 12,
                        background:
                          'var(--color-error-container)',
                        color:
                          'var(--color-error)',
                        fontWeight: 700,
                        fontSize: 15,
                      }}
                    >
                      Cancel Session
                    </button>
                  )}
                </div>
              </div>
            </FadeInAnimation>
          </div>
        </div>
      </div>

      {/* CANCEL DIALOG */}
      <Dialog
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
      >
        <h3
          style={{
            fontSize: 20,
            fontWeight: 700,
            marginBottom: 8,
          }}
        >
          Cancel Session
        </h3>

        <p
          style={{
            fontSize: 14,
            color:
              'var(--color-on-surface-variant)',
            marginBottom: 18,
            lineHeight: 1.6,
          }}
        >
          Please provide a reason for cancelling
          this session.
        </p>

        <textarea
          className="input"
          style={{
            minHeight: 100,
            resize: 'vertical',
            marginBottom: 18,
          }}
          placeholder="Reason for cancellation..."
          value={cancelReason}
          onChange={(e) =>
            setCancelReason(e.target.value)
          }
        />

        <div
          style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <button
            onClick={() => setCancelOpen(false)}
            style={{
              flex: 1,
              minWidth: 120,
              height: 48,
              borderRadius: 10,
              background:
                'var(--color-surface-variant)',
              color:
                'var(--color-on-surface-variant)',
              fontWeight: 600,
            }}
          >
            Go Back
          </button>

          <button
            onClick={handleCancel}
            style={{
              flex: 1,
              minWidth: 120,
              height: 48,
              borderRadius: 10,
              background: 'var(--color-error)',
              color: 'white',
              fontWeight: 700,
            }}
          >
            Confirm Cancel
          </button>
        </div>
      </Dialog>

      {/* RATING DIALOG */}
      <Dialog
        open={ratingOpen}
        onClose={() => setRatingOpen(false)}
      >
        <h3
          style={{
            fontSize: 20,
            fontWeight: 700,
            marginBottom: 4,
          }}
        >
          Rate Your Session
        </h3>

        <p
          style={{
            fontSize: 14,
            color:
              'var(--color-on-surface-variant)',
            marginBottom: 20,
          }}
        >
          How was your session with{' '}
          {session.therapistName}?
        </p>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 10,
            marginBottom: 20,
            flexWrap: 'wrap',
          }}
        >
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onClick={() => setRating(star)}
              style={{
                background: 'none',
                fontSize: 36,
              }}
            >
              {star <= rating ? '⭐' : '☆'}
            </button>
          ))}
        </div>

        <textarea
          className="input"
          style={{
            minHeight: 90,
            resize: 'vertical',
            marginBottom: 18,
          }}
          placeholder="Leave a review (optional)..."
          value={review}
          onChange={(e) =>
            setReview(e.target.value)
          }
        />

        <div
          style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <button
            onClick={() => setRatingOpen(false)}
            style={{
              flex: 1,
              minWidth: 120,
              height: 48,
              borderRadius: 10,
              background:
                'var(--color-surface-variant)',
              color:
                'var(--color-on-surface-variant)',
              fontWeight: 600,
            }}
          >
            Skip
          </button>

          <button
            onClick={handleRate}
            style={{
              flex: 1,
              minWidth: 120,
              height: 48,
              borderRadius: 10,
              background:
                'var(--color-primary)',
              color: 'white',
              fontWeight: 700,
            }}
          >
            Submit
          </button>
        </div>
      </Dialog>
    </div>
  );
};

const SectionTitle: React.FC<{
  title: string;
}> = ({ title }) => (
  <div
    style={{
      fontWeight: 700,
      fontSize: 13,
      color: 'var(--color-primary)',
      marginBottom: 16,
      textTransform: 'uppercase',
      letterSpacing: 0.8,
    }}
  >
    {title}
  </div>
);

const InfoRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
}> = ({ icon, label, value }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 14,
    }}
  >
    <div
      style={{
        width: 42,
        height: 42,
        borderRadius: 12,
        background:
          'var(--color-primary-container)',
        color:
          'var(--color-on-primary-container)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </div>

    <div style={{ flex: 1 }}>
      <div
        style={{
          fontSize: 11,
          color:
            'var(--color-on-surface-variant)',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          marginBottom: 4,
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 15,
          fontWeight: 600,
          color: 'var(--color-on-surface)',
        }}
      >
        {value}
      </div>
    </div>
  </div>
);

const Divider: React.FC = () => (
  <div
    style={{
      height: 1,
      background: 'var(--color-outline-variant)',
    }}
  />
);

const CalIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect
      x="3"
      y="4"
      width="18"
      height="18"
      rx="2"
    />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const TimeIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

export default UpcomingDetailsPage;